import { Flex } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";

import ButtonComponent from "@/components/button/Button";
import ReviewModal from "@/components/form/ReviewModal";
import { createGuideReviewRating, createSessionReviewRating } from "@/services/reviews";
import { getSessionDetailById } from "@/services/common/getSessionDetailById";
import { getIsGuidePanel } from "@/reducers/guideSlice";
import { showNotification } from "@/components/toaster/Toaster";

const CallEnd = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const sessionId = params.get("sid");
  const isGuide = useSelector(getIsGuidePanel);

  const [session, setSession] = useState(null);

  // Review-key helpers
  const reviewKeys = useMemo(() => {
    const base = (type) => ({
      submitted: `review_submitted_${sessionId}_${type}`,
      skipped: `review_skipped_${sessionId}_${type}`,
    });
    return { session: base("session"), guide: base("guide") };
  }, [sessionId]);

  const isPending = useCallback(
    (type) =>
      !localStorage.getItem(reviewKeys[type].submitted) &&
      !localStorage.getItem(reviewKeys[type].skipped),
    [reviewKeys]
  );

  const getPendingQueue = useCallback(() => {
    const q = [];
    if (isPending("session")) q.push("session");
    if (!isGuide && isPending("guide")) q.push("guide");
    return q;
  }, [isGuide, isPending]);

  // State
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null); // "session" | "guide" | null
  const [modalOpen, setModalOpen] = useState(false);

  // Core transition helper
  const proceed = useCallback((nextQueue) => {
    // 1. close whatever is showing
    setModalOpen(false);

    // 2. after exit-animation, advance
    setTimeout(() => {
      setQueue(nextQueue);

      if (nextQueue.length) {
        setCurrent(nextQueue[0]);
        setModalOpen(true); // ‼️ opens AFTER close
      } else {
        setCurrent(null);
        // flow finished
        // showEndCallConfirmation?.();
      }
    }, 300); // match your modal exit duration
  }, []);

  // Fetch session details

  useEffect(() => {
    (async () => {
      try {
        const data = await getSessionDetailById(sessionId);
        setSession(data);
      } catch (err) {
        console.error("Failed to fetch session details:", err);
      }
    })();
  }, [sessionId]);

  // Build initial queue once we have session data
  useEffect(() => {
    if (!session) return;
    proceed(getPendingQueue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Handlers

  const handleSubmit = useCallback(
    async ({ rating, review }) => {
      try {
        if (!current || !isPending(current)) return proceed(getPendingQueue());

        if (current === "session") {
          await createSessionReviewRating({ sessionId, rating, review });
          localStorage.setItem(reviewKeys.session.submitted, "true");
        } else {
          await createGuideReviewRating({ guideId: session?.guideId, rating, review });
          localStorage.setItem(reviewKeys.guide.submitted, "true");
        }
        showNotification("success", "Thank you for your review!");
      } catch (err) {
        console.error("Error submitting review:", err);
        showNotification("error", "Error", "Failed to submit your review. Please try again.");
      } finally {
        proceed(queue.slice(1));
      }
    },
    [current, queue, isPending, reviewKeys, sessionId, session?.guideId, getPendingQueue, proceed]
  );

  const handleSkip = useCallback(() => {
    if (current) localStorage.setItem(reviewKeys[current].skipped, "true");
    proceed(queue.slice(1));
  }, [current, queue, reviewKeys, proceed]);


  const description =
    current === "guide"
      ? "Rate your guide’s support and guidance. Let them know how they did, your input matters!"
      : "How was your session experience? Your feedback helps us improve future sessions.";

  return (
    <Flex
      justify="center"
      align="center"
      style={{ height: 300, paddingBottom: 10 }}
      vertical
      gap={40}
    >
      <Flex vertical gap={10} align="center">
        <div className="body-b1">Your Session is Completed</div>

        <Flex
          className="add-note"
          style={{
            background: "#fff",
            marginTop: "1rem",
            padding: "1rem",
            borderRadius: 8,
            border: "1px solid #E5E5E5",
          }}
        >
          <p className="body-b3">
            <span style={{ fontWeight: 500 }}>Note:&nbsp;</span>
            The session recording and transcription will be available soon—​we’ll notify you.
          </p>
        </Flex>

        <Flex gap={10}>
          <ButtonComponent
            label="Go to Dashboard"
            varient="primary"
            onClick={() => navigate("/dashboard")}
          />
          <ButtonComponent label="Go to Sessions" onClick={() => navigate("/sessions")} />
        </Flex>
      </Flex>

      {current && (
        <ReviewModal
          isOpen={modalOpen}
          description={description}
          btnLabel="Submit Review"
          userId={sessionId}
          guideId={session?.guideId}
          onSubmit={handleSubmit}
          onCancelClick={handleSkip}
        />
      )}
    </Flex>
  );
};

export default CallEnd;
